import { getIdToken } from '@pooflabs/web';
import { Loader2, Square, Star, Zap } from 'lucide-react';
import React, { useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { createAuthenticatedApiClient } from '@/lib/api-client';
import { subscribeGpuNodes, type GpuNodesResponse } from '@/lib/collections/gpuNodes';
import type { RentalsResponse } from '@/lib/collections/rentals';
import { useRealtimeData } from '@/hooks/use-realtime-data';
import { formatSol } from '@/utils/format';
import { getTierMeta, reputationPercent } from '@/utils/gpuNetwork';

interface ActiveRentalCardProps {
  rental: RentalsResponse;
  walletAddress: string;
  balanceLamports: number;
  balanceLoading: boolean;
}

/**
 * Live display-only counter for the renter's own active rental. Mirrors the
 * backend's cost formula (elapsedSeconds * ratePerSecLamports) purely for
 * the UI — the backend always recomputes cost from stored startedAt/
 * ratePerSecLamports at settle time, capped at the real ledger balance.
 * When the live "remaining" hits zero, auto-stops so the renter never
 * appears to keep paying past their funded balance.
 */
export const ActiveRentalCard: React.FC<ActiveRentalCardProps> = ({
  rental,
  walletAddress,
  balanceLamports,
  balanceLoading,
}) => {
  const { data: node } = useRealtimeData<GpuNodesResponse | null>(subscribeGpuNodes, true, rental.nodeId);

  const [nowSeconds, setNowSeconds] = useState(() => Math.floor(Date.now() / 1000));
  const [stopping, setStopping] = useState(false);
  const stoppingRef = useRef(false);

  useEffect(() => {
    const id = setInterval(() => setNowSeconds(Math.floor(Date.now() / 1000)), 1000);
    return () => clearInterval(id);
  }, []);

  const elapsedSeconds = Math.max(0, nowSeconds - rental.startedAt);
  const costLamports = elapsedSeconds * rental.ratePerSecLamports;
  const remainingLamports = Math.max(0, balanceLamports - costLamports);

  const handleStop = async () => {
    if (stoppingRef.current) return;
    stoppingRef.current = true;
    setStopping(true);
    try {
      const token = await getIdToken();
      if (!token) {
        toast.error('Please reconnect your wallet and try again.');
        return;
      }
      const authApi = createAuthenticatedApiClient(token, walletAddress);
      await authApi.post(`/api/rentals/${rental.id}/stop`);
      toast.success('Rental stopped.');
    } catch (err) {
      console.error(err);
      toast.error(err instanceof Error && err.message ? err.message : 'Could not stop the rental.');
    } finally {
      setStopping(false);
      stoppingRef.current = false;
    }
  };

  // Auto-stop once the live balance would be exhausted so the renter never
  // appears to keep accruing cost past what they've funded.
  const exhausted = !balanceLoading && remainingLamports <= 0;
  useEffect(() => {
    if (exhausted) {
      handleStop();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [exhausted]);

  return (
    <div className="rounded-3xl border border-primary/30 bg-accent/60 p-5 relative overflow-hidden">
      <div
        className="absolute inset-x-0 top-0 h-1 opacity-80"
        style={{ background: 'linear-gradient(90deg, transparent, hsl(var(--primary)), transparent)' }}
      />
      <p className="text-[11px] font-semibold text-accent-foreground/70 uppercase tracking-wide flex items-center gap-1">
        <Zap className="w-3 h-3 animate-pulse" /> Active deployment
      </p>
      <p className="font-extrabold text-lg mt-1 truncate text-accent-foreground">
        {node?.label || node?.gpuModel || 'Loading…'}
      </p>

      <div className="flex items-center gap-1.5 mt-1 flex-wrap">
        {node && (
          <span className="inline-flex items-center gap-1 rounded-full bg-background/60 px-2 py-0.5 text-[11px] font-semibold text-accent-foreground/80">
            {getTierMeta(node.tier).label}
          </span>
        )}
        {node && (
          <span className="inline-flex items-center gap-1 rounded-full bg-background/60 px-2 py-0.5 text-[11px] font-semibold text-accent-foreground/80">
            <Star className="w-3 h-3 fill-current text-amber-400" /> {reputationPercent(node.reputationScore)}%
          </span>
        )}
      </div>

      {rental.containerImage && (
        <p className="mt-2 truncate rounded-lg bg-background/60 px-2.5 py-1.5 text-[11px] font-mono text-accent-foreground/80">
          {rental.containerImage}
        </p>
      )}

      <div className="grid grid-cols-2 gap-2.5 mt-3">
        <div className="rounded-2xl bg-background/60 p-3">
          <p className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">Cost so far</p>
          <p className="text-sm font-extrabold mt-0.5 tabular-nums">{formatSol(costLamports, 6)}</p>
        </div>
        <div className="rounded-2xl bg-background/60 p-3">
          <p className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">Balance left</p>
          <p className="text-sm font-extrabold mt-0.5 tabular-nums">{formatSol(remainingLamports, 6)}</p>
        </div>
      </div>

      <button
        onClick={handleStop}
        disabled={stopping}
        className="mt-4 w-full rounded-full bg-foreground text-background font-bold py-2.5 text-sm hover:opacity-90 transition-all duration-300 disabled:opacity-50 flex items-center justify-center gap-1.5"
      >
        {stopping ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Square className="w-3.5 h-3.5" />}
        {stopping ? 'Stopping…' : 'Stop rental'}
      </button>
    </div>
  );
};

export default ActiveRentalCard;
